// 路由表：path → {feature, title, anyOf}。
// 业务页加载机制：import.meta.glob 扫描 src/features/*\/index.tsx，
// 文件存在则 React.lazy 真实页面，缺失则渲染 PlaceholderPage——
// 后续页面智能体只需新建 src/features/<feature>/index.tsx（默认导出页面组件），路由零改动。
// anyOf 与 features/shell/nav.ts 导航 NAV_GROUPS 的可见性保持一致；服务端仍有独立鉴权，前端守卫只做体验层。

import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from "react";
import { Link, Route, Routes } from "react-router-dom";
import { CAPABILITIES, type Capability } from "../shared/capabilities";
import { AppLayout } from "./features/shell/AppLayout";
import { RequireAuth, RequireCapability } from "./lib/auth";
import { Spinner } from "@bf/ui";
import { PlaceholderPage } from "./features/_placeholder";
import { ForbiddenPage } from "./features/auth/ForbiddenPage";
import { LoginCallbackPage } from "./features/auth/LoginCallbackPage";
import { LoginPage } from "./features/auth/LoginPage";

interface RouteDef {
  path: string;
  /** src/features/<feature>/index.tsx 的目录名 */
  feature: string;
  /** 页面中文名（占位页展示用） */
  title: string;
  /** 任一能力命中可访问 */
  anyOf: Capability[];
}

const ROUTE_TABLE: RouteDef[] = [
  { path: "/", feature: "dashboard", title: "工作台", anyOf: ["employee"] },
  { path: "/claims", feature: "claims", title: "申报中心", anyOf: ["employee"] },
  { path: "/approvals", feature: "approvals", title: "审批中心", anyOf: ["dept_head", "ops", "decide", "review", "verify", "admin"] },
  { path: "/dept-contribution", feature: "dept-contribution", title: "部门周期贡献", anyOf: ["dept_head", "ops", "admin"] },
  { path: "/projects", feature: "projects", title: "项目积分", anyOf: ["employee"] },
  { path: "/innovation", feature: "innovation", title: "创新成果", anyOf: ["employee"] },
  { path: "/contests", feature: "contests", title: "竞赛", anyOf: ["employee"] },
  { path: "/honors", feature: "honors", title: "荣誉表彰", anyOf: ["employee"] },
  { path: "/committee", feature: "committee", title: "委员积分", anyOf: ["employee"] },
  { path: "/adhoc", feature: "adhoc", title: "一事一议", anyOf: ["employee"] },
  { path: "/deductions", feature: "deductions", title: "违纪扣分", anyOf: ["dept_head", "ops", "decide", "verify", "admin"] },
  { path: "/redemptions", feature: "redemptions", title: "兑换中心", anyOf: ["employee"] },
  { path: "/ledger", feature: "ledger", title: "台账", anyOf: ["ops", "decide", "review", "verify", "admin"] },
  { path: "/publicity", feature: "publicity", title: "公示", anyOf: ["employee"] },
  { path: "/ranking", feature: "ranking", title: "排行榜", anyOf: ["employee"] },
  { path: "/budget", feature: "budget", title: "预算总览", anyOf: ["ops", "decide", "verify", "admin"] },
  { path: "/admin", feature: "admin", title: "系统管理", anyOf: ["admin"] },
  { path: "/admin/agent-logs", feature: "agent-logs", title: "Agent 调用日志", anyOf: ["admin"] },
  { path: "/tools/api-keys", feature: "api-keys", title: "API 密钥", anyOf: [...CAPABILITIES] },
  { path: "/account", feature: "account", title: "个人中心", anyOf: ["employee"] },
];

type PageModule = { default: ComponentType };

// vite 构建期静态分析：已存在的 features 页打独立 chunk，未存在的不出现在表里
const featurePages = import.meta.glob<PageModule>("./features/*/index.tsx");

function pageComponent(def: RouteDef): LazyExoticComponent<ComponentType> | ComponentType {
  const loader = featurePages[`./features/${def.feature}/index.tsx`];
  if (loader) return lazy(loader);
  return () => <PlaceholderPage name={def.title} />;
}

function PageLoading() {
  return (
    <div className="flex items-center justify-center py-24">
      <Spinner />
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-bg)] p-6">
      <p className="text-4xl font-semibold text-[var(--color-faint)]">404</p>
      <p className="mt-3 text-base font-medium text-[var(--color-ink)]">页面不存在</p>
      <Link to="/" className="mt-6 text-sm text-[var(--color-primary)] hover:text-[var(--color-primary-hover)]">
        返回工作台
      </Link>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/callback" element={<LoginCallbackPage />} />
      <Route path="/403" element={<ForbiddenPage />} />
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        {ROUTE_TABLE.map((def) => {
          const Page = pageComponent(def);
          return (
            <Route
              key={def.path}
              path={def.path}
              element={
                <RequireCapability anyOf={def.anyOf}>
                  <Suspense fallback={<PageLoading />}>
                    <Page />
                  </Suspense>
                </RequireCapability>
              }
            />
          );
        })}
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
